<template>
  <b-card
    no-body
    class="h-100 order-planner-backlog-card"
  >
    <div class="p-1 border-bottom">
      <div class="font-weight-bolder order-planner-backlog-card__title">
        {{ $t('order.planner.backlog-title') }}
      </div>
    </div>

    <div class="p-1 overflow-auto order-planner-backlog">
      <div
        v-if="orders.length === 0"
        class="text-muted small"
      >
        {{ $t('order.planner.empty-backlog') }}
      </div>

      <div
        v-for="order in orders"
        :key="order.id"
        class="mb-1"
      >
        <div
          :data-order-id="order.id"
          class="order-planner-backlog__item rounded-sm p-1"
          draggable="true"
          @dragstart="handleItemDragStart(order, $event)"
        >
          <div class="d-flex align-items-start justify-content-between mb-75">
            <b-link
              :to="{ name: 'order-view', params: { id: createBacklogOrder(order).id } }"
              class="font-weight-bolder font-medium-1 text-primary pr-50"
            >
              {{ createBacklogOrder(order).fullNumber || '-' }}
            </b-link>

            <div class="d-flex align-items-center flex-shrink-0">
              <b-avatar
                v-if="createBacklogOrder(order).prioritySignals"
                v-b-tooltip.hover.top="$t('order.priority-signals.true')"
                size="18"
                variant="primary"
                class="ml-25"
              >
                <feather-icon
                  icon="LoaderIcon"
                  size="12"
                />
              </b-avatar>
              <b-avatar
                v-if="createBacklogOrder(order).absolutePunctuality"
                v-b-tooltip.hover.top="$t('order.label.base-order-info.absolute-punctuality')"
                size="18"
                variant="danger"
                class="ml-25"
              >
                <feather-icon
                  icon="ClockIcon"
                  size="12"
                />
              </b-avatar>
              <b-avatar
                v-if="createBacklogOrder(order).infectious"
                v-b-tooltip.hover.top="$t('order.label.infectious')"
                size="18"
                variant="warning"
                class="ml-25"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="12"
                />
              </b-avatar>
              <b-avatar
                v-if="createBacklogOrder(order).private"
                v-b-tooltip.hover.top="$t('order.label.private-order')"
                size="18"
                variant="success"
                class="ml-25"
              >
                <feather-icon
                  icon="DollarSignIcon"
                  size="12"
                />
              </b-avatar>
            </div>
          </div>

          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center text-muted">
              <feather-icon
                :id="getBacklogRoutePopoverTarget(createBacklogOrder(order))"
                icon="MapIcon"
                size="16"
                class="mr-50"
              />
              <feather-icon
                v-if="createBacklogOrder(order).returnTransport"
                v-b-tooltip.hover.top="$t('order.label.base-order-info.return-transport')"
                icon="RepeatIcon"
                size="16"
                class="mr-50"
              />
              <b-popover
                :target="getBacklogRoutePopoverTarget(createBacklogOrder(order))"
                triggers="hover focus"
              >
                <div class="planning-route-popover">
                  <div class="planning-route-popover__section">
                    <div class="planning-route-popover__label text-muted">
                      {{ $t('order.label.pickup-address') }}
                    </div>
                    <div
                      v-if="getBacklogAddressParts(createBacklogOrder(order).pickupAddress)"
                      class="planning-route-popover__value"
                    >
                      <div v-if="getBacklogAddressParts(createBacklogOrder(order).pickupAddress).isAddressPrimary">
                        <div class="font-small-4">
                          {{ getBacklogAddressParts(createBacklogOrder(order).pickupAddress).addressLine }}
                        </div>
                      </div>
                      <div
                        v-else
                        :class="{ 'mb-25': getBacklogAddressParts(createBacklogOrder(order).pickupAddress).addressLine }"
                      >
                        <span
                          v-if="getBacklogAddressParts(createBacklogOrder(order).pickupAddress).alias"
                          class="font-small-4 font-weight-bold d-block"
                        >
                          {{ getBacklogAddressParts(createBacklogOrder(order).pickupAddress).alias }}
                        </span>
                        <span
                          v-if="getBacklogAddressParts(createBacklogOrder(order).pickupAddress).name"
                          class="text-body d-block"
                        >
                          {{ getBacklogAddressParts(createBacklogOrder(order).pickupAddress).name }}
                        </span>
                      </div>
                      <small
                        v-if="getBacklogAddressParts(createBacklogOrder(order).pickupAddress).addressLine && !getBacklogAddressParts(createBacklogOrder(order).pickupAddress).isAddressPrimary"
                        class="text-muted d-block"
                      >
                        {{ getBacklogAddressParts(createBacklogOrder(order).pickupAddress).addressLine }}
                      </small>
                    </div>
                    <div
                      v-else
                      class="planning-route-popover__value font-small-4"
                    >
                      {{ $t('shared.no-data') }}
                    </div>
                  </div>

                  <div class="planning-route-popover__section">
                    <div class="planning-route-popover__label text-muted">
                      {{ $t('order.label.delivery-address') }}
                    </div>
                    <div
                      v-if="getBacklogAddressParts(createBacklogOrder(order).deliveryAddress)"
                      class="planning-route-popover__value"
                    >
                      <div v-if="getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).isAddressPrimary">
                        <div class="font-small-4">
                          {{ getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).addressLine }}
                        </div>
                      </div>
                      <div
                        v-else
                        :class="{ 'mb-25': getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).addressLine }"
                      >
                        <span
                          v-if="getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).alias"
                          class="font-small-4 font-weight-bold d-block"
                        >
                          {{ getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).alias }}
                        </span>
                        <span
                          v-if="getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).name"
                          class="text-body d-block"
                        >
                          {{ getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).name }}
                        </span>
                      </div>
                      <small
                        v-if="getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).addressLine && !getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).isAddressPrimary"
                        class="text-muted d-block"
                      >
                        {{ getBacklogAddressParts(createBacklogOrder(order).deliveryAddress).addressLine }}
                      </small>
                    </div>
                    <div
                      v-else
                      class="planning-route-popover__value font-small-4"
                    >
                      {{ $t('shared.no-data') }}
                    </div>
                  </div>
                </div>
              </b-popover>
            </div>

            <b-badge
              variant="light-secondary"
              class="font-medium-1"
            >
              {{ transformDatetimeToHumanReadablePretty(createBacklogOrder(order).startAt) }}
            </b-badge>
          </div>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import { defineComponent } from '@vue/composition-api'
import {
  BAvatar, BBadge, BCard, BLink, BPopover, VBTooltip,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'

export default defineComponent({
  name: 'OrderPlannerBacklog',
  components: {
    BAvatar, BBadge, BCard, BLink, BPopover, FeatherIcon,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    orders: { type: Array, default: () => [] },
  },
  setup() {
    const createBacklogOrder = order => ({
      ...(order?.original || {}),
      id: Number(order?.original?.id || order?.id),
      fullNumber: order?.original?.fullNumber || order?.title,
      startAt: order?.original?.startAt || order?.start,
    })

    const getBacklogRoutePopoverTarget = order => `order-planner-backlog-route-${order?.id || 'unknown'}`

    const getBacklogAddressParts = address => {
      if (!address || typeof address !== 'object') return null
      const alias = address.alias || ''
      const name = address.name || ''
      const addressLine = [address.city, address.address].filter(Boolean).join(', ')
      if (alias || name) {
        return {
          alias, name, addressLine, isAddressPrimary: false,
        }
      }
      if (addressLine) {
        return {
          alias: '', name: '', addressLine, isAddressPrimary: true,
        }
      }
      return null
    }

    const handleItemDragStart = (order, event) => {
      // eslint-disable-next-line no-param-reassign
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', JSON.stringify(order))
    }

    return {
      transformDatetimeToHumanReadablePretty,
      createBacklogOrder,
      getBacklogRoutePopoverTarget,
      getBacklogAddressParts,
      handleItemDragStart,
    }
  },
})
</script>

<style lang="scss" scoped>
.order-planner-backlog-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.order-planner-backlog-card__title {
  white-space: nowrap;
}

.order-planner-backlog {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 60rem;
}

.order-planner-backlog__item {
  border: 1px solid rgba(100, 116, 139, 0.32);
  border-left: 0.35rem solid #0f9f6e;
  background: linear-gradient(180deg, #f8fafc 0%, #edf2f7 52%, #e2e8f0 100%);
  box-shadow: 0 0.2rem 0.8rem rgba(15, 23, 42, 0.12);
  cursor: grab;
  color: #334155;
  touch-action: none;
  user-select: none;
}

.order-planner-backlog__item .badge-light-secondary {
  background: rgba(255, 255, 255, 0.72);
  color: #334155;
}

.planning-route-popover__section + .planning-route-popover__section {
  margin-top: 0.75rem;
}

.planning-route-popover__label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

.dark-layout .order-planner-backlog__item {
  border-color: rgba(148, 163, 184, 0.2);
  border-left-color: #34d399;
  background: linear-gradient(180deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%);
  box-shadow: 0 0.25rem 1rem rgba(2, 6, 23, 0.35);
  color: #e2e8f0;
}

.dark-layout .order-planner-backlog__item .text-muted,
.dark-layout .order-planner-backlog__item .badge-light-secondary {
  color: #cbd5e1 !important;
}

.dark-layout .order-planner-backlog__item .badge-light-secondary {
  background: rgba(148, 163, 184, 0.16) !important;
  border: 1px solid rgba(148, 163, 184, 0.18);
}

@media (min-width: 1200px) {
  .order-planner-backlog {
    max-height: none;
  }
}
</style>
